import { FunctionComponent, memo, useCallback, useEffect, useMemo } from 'react'
import { FintecWidget } from './FintecWidget'
import { useStorage } from '../hooks/useStorage'
import { ErrorCode } from '../constants/errorCodes'
import { FintecProps, FintecState } from '../types/tink.interface'
import { useTheme } from '../hooks/useTheme'
import { TinkLoader } from './TinkLoader'
import FinTechWidgetProvider from '../context'
import { TrackProvider } from '../context/track'
import { DACSteps } from '../constants/steps'
import { ErrorBoundary } from './ErrorBoundary'
import { logEvent } from '../utils/bugsnag/log'
import { BugSnagTrackingEvent } from '../utils/bugsnag/events'

const FINTEC_STATE_STORAGE_KEY = 'fts_widget_state'

const renderlessStates: FintecState[] = ['initial', 'success', 'disagreed']

const Fintec: FunctionComponent<FintecProps> = memo(
  ({
    system,
    config,
    account,
    onError,
    onTrack,
    onAbort,
    onSuccess,
    children,
    onStateChange,
    skipIbanGate,
    onStepChange,
  }) => {
    const {
      clientUuid,
      checkPeriodDays = 'long_term',
      personId,
      traversalUuid,
    } = system

    useTheme({ clientUuid })
    const [state, setState, clearState] = useStorage<FintecState>({
      type: 'sessionStorage',
      key: FINTEC_STATE_STORAGE_KEY,
      initialValue: 'initial',
    })

    const handleError = useCallback(
      (code: ErrorCode, messages: string[], isRecoverable: boolean) => {
        if (onError) {
          onError(code, messages, isRecoverable)
        }

        setState('error')

        if (isRecoverable) {
          // TODO: Restart
        }
        clearState()
      },
      [clearState, onError, setState],
    )
    const handleAbort = useCallback(() => {
      if (onAbort) {
        onAbort()
      }

      setState('aborted')
      clearState()
    }, [clearState, onAbort, setState])

    const handleSuccess = useCallback(
      (transactionId: string) => {
        logEvent(
          'OnSuccess callback handler in fintech',
          BugSnagTrackingEvent.OnSuccessCallback,
          {
            clientUuid,
            personId,
            origin,
            traversalUuid,
            transactionId,
          },
        )
        if (onSuccess) {
          onSuccess(transactionId)
        }
        onStepChange?.(DACSteps.DAC_SUCCESS_STEP)
        setState('success')
        clearState()
      },
      [
        clientUuid,
        personId,
        traversalUuid,
        onSuccess,
        onStepChange,
        setState,
        clearState,
      ],
    )

    const widget = useMemo(() => {
      if (renderlessStates.includes(state)) {
        return null
      }

      return (
        <ErrorBoundary>
          <TrackProvider
            onTrack={(event, data) => {
              const w = window as any
              if (onTrack) {
                onTrack(event, data)
              } else if (w?.analytics) {
                w.analytics.track(`fts-widet-${event}`, data)
              } else {
                console.log('onTrack is not defined', { event, data })
              }
            }}
          >
            <FinTechWidgetProvider system={{ ...system, checkPeriodDays }}>
              <TinkLoader onError={handleError}>
                <FintecWidget
                  account={account}
                  system={system}
                  config={config}
                  skipIbanGate={skipIbanGate}
                  onError={handleError}
                  onSuccess={handleSuccess}
                  onAbort={handleAbort}
                  onStepChange={onStepChange}
                />
              </TinkLoader>
            </FinTechWidgetProvider>
          </TrackProvider>
        </ErrorBoundary>
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state])

    useEffect(() => {
      if (onStateChange) {
        onStateChange(state)
      }
    }, [onStateChange, state])

    return children({
      state,
      setState,
      widget,
    })
  },
)

Fintec.displayName = 'Fintec'

export { Fintec }
